import React, { useState, useEffect, useRef } from 'react';
import { Send, Loader2, AlertCircle, RotateCcw, PenTool, Paperclip, X, File, Copy, Check, FileText } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';

const API_URL = process.env.REACT_APP_API_URL || process.env.VITE_API_URL || 'http://localhost:8000';

const STORAGE_KEY = 'writing-assistant-chat';

function Write() {
  const { getToken } = useAuth();
  const [draft, setDraft] = useState('');
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [messages, setMessages] = useState(() => {
    // Load messages from localStorage on mount
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.map(msg => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
      }
    } catch (err) {
      console.error('Failed to load chat from localStorage:', err);
    }
    return [];
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const chatEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [copiedMessageId, setCopiedMessageId] = useState(null);

  // Save messages to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    } catch (err) {
      console.error('Failed to save chat to localStorage:', err);
    }
  }, [messages]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, loading]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [draft]);

  const examplePrompts = [
    'Draft a professional email to a client about a shipment',
    'Write a response to a customer inquery',
    'Draft a reply to a supplier quote request',
    'Help me write a follow-up email after a sales call',
  ];

  const formatTime = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!draft.trim()) return;

    const currentPrompt = draft.trim();
    const promptId = Date.now();
    
    // Add user message
    // Capture attached files before clearing
    const filesToAttach = [...attachedFiles];
    
    setMessages((prev) => [
      ...prev,
      {
        id: promptId,
        role: 'user',
        text: currentPrompt,
        timestamp: new Date(),
        attachedFiles: filesToAttach.length > 0 ? filesToAttach.map(f => ({
          name: f.file.name,
          size: f.file.size,
          type: f.file.type
        })) : null,
      },
    ]);

    setDraft('');
    setError('');
    // Clear attached files after sending
    setAttachedFiles([]);
    
    // Reset textarea height after clearing
    if (textareaRef.current) {
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
        }
      }, 0);
    }

    // Show loading states immediately
    setLoading(true);
    
    // OPTIMIZATION: Make request immediately, don't wait for other operations
    setTimeout(async () => {
      try {
        
        // Build conversation history from previous messages
        const conversationHistory = messages
          .filter(msg => msg.role === 'user' || msg.role === 'assistant')
          .map(msg => ({
            role: msg.role,
            content: msg.text
          }));

        // Helper function to make authenticated request with token refresh
        const makeAuthenticatedRequest = async (retryCount = 0) => {
          const token = await getToken();
          if (!token) {
            throw new Error('Authentication required. Please sign in.');
          }
          
          // If files are attached, use FormData, otherwise use JSON
          let body;
          let headers = {
            'Authorization': `Bearer ${token}`,
          };
          
          if (attachedFiles.length > 0) {
            // Use FormData for file uploads
            const formData = new FormData();
            formData.append('prompt', currentPrompt);
            formData.append('conversation_history', JSON.stringify(conversationHistory));
            
            // Add files
            attachedFiles.forEach(file => {
              formData.append('files', file.file);
            });
            
            body = formData;
            // Don't set Content-Type header - browser will set it with boundary for FormData
          } else {
            // Use JSON when no files
            headers['Content-Type'] = 'application/json';
            body = JSON.stringify({ 
              prompt: currentPrompt,
              conversation_history: conversationHistory
            });
          }
          
          const response = await fetch(`${API_URL}/api/write`, {
            method: 'POST',
            headers: headers,
            body: body,
          });

          // Handle 401 - token expired, try refreshing once
          if (response.status === 401 && retryCount === 0) {
            await new Promise(resolve => setTimeout(resolve, 100));
            return makeAuthenticatedRequest(1);
          }

          if (!response.ok) {
            const data = await response.json().catch(() => ({ detail: 'Request failed' }));
            const errorMsg = data.detail || 'Writing request failed';
            
            // Handle rate limiting
            if (response.status === 429) {
              throw new Error(errorMsg);
            }
            
            // Handle other errors
            throw new Error(errorMsg);
          }
          
          return response;
        };
        
        const response = await makeAuthenticatedRequest();
        
        // Check if response is streaming (text/event-stream)
        const contentType = response.headers.get('content-type') || '';
        
        if (contentType.includes('text/event-stream')) {
          // Handle streaming response
          const responseId = Date.now() + 1;
          let ragDocuments = null;
          let fullText = '';
          
          // Create assistant message immediately
          setMessages((prev) => [
            ...prev,
            {
              id: responseId,
              role: 'assistant',
              text: '',
              timestamp: new Date(),
              ragDocuments: null,
            },
          ]);
          
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          
          try {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              
              buffer += decoder.decode(value, { stream: true });
              const lines = buffer.split('\n');
              buffer = lines.pop() || ''; // Keep incomplete line in buffer
              
              for (const line of lines) {
                if (line.startsWith('data: ')) {
                  try {
                    const data = JSON.parse(line.slice(6));
                    
                    if (data.type === 'start') {
                      // Capture the value immediately to avoid closure issues
                      const currentRagDocuments = data.rag_documents;
                      ragDocuments = currentRagDocuments;
                      // Stop showing loading dots once streaming starts
                      setLoading(false);
                      // Update message with RAG documents
                      setMessages((prev) => 
                        prev.map(msg => 
                          msg.id === responseId 
                            ? { ...msg, ragDocuments: currentRagDocuments }
                            : msg
                        )
                      );
                    } else if (data.type === 'chunk') {
                      fullText += data.content;
                      // Capture the current fullText value to avoid closure issues
                      const currentFullText = fullText;
                      // Update message text progressively
                      setMessages((prev) => 
                        prev.map(msg => 
                          msg.id === responseId 
                            ? { ...msg, text: currentFullText }
                            : msg
                        )
                      );
                    } else if (data.type === 'done') {
                      // Final cleaned text - capture both values
                      const finalText = data.content;
                      const finalRagDocuments = ragDocuments;
                      fullText = finalText;
                      // Preserve ragDocuments when updating final text
                      setMessages((prev) => 
                        prev.map(msg => 
                          msg.id === responseId 
                            ? { ...msg, text: finalText, ragDocuments: finalRagDocuments }
                            : msg
                        )
                      );
                      setLoading(false); // Stop loading when done
                      break;
                    } else if (data.type === 'error') {
                      throw new Error(data.error || 'Streaming error occurred');
                    }
                  } catch (e) {
                    console.error('Error parsing SSE data:', e);
                  }
                }
              }
            }
          } catch (streamError) {
            setError(streamError.message || 'Error during streaming response');
            setLoading(false);
            // Update message with error
            setMessages((prev) => 
              prev.map(msg => 
                msg.id === responseId 
                  ? { ...msg, text: fullText || 'Error generating response' }
                  : msg
              )
            );
          }
        } else {
          // Handle non-streaming response (fallback)
          const data = await response.json();
          const responseId = Date.now() + 1;
          setMessages((prev) => [
            ...prev,
            {
              id: responseId,
              role: 'assistant',
              text: data.content || "I couldn't generate content for that request.",
              timestamp: new Date(),
              ragDocuments: data.rag_documents || null,
            },
          ]);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }, 0); // Execute immediately after current call stack
  };

  const handleExampleClick = (prompt) => {
    setDraft(prompt);
  };

  const handleReset = () => {
    setTimeout(() => {
      setMessages([]);
      setError('');
      setDraft('');
      setAttachedFiles([]);
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
      // Clear localStorage
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch (err) {
        console.error('Failed to clear localStorage:', err);
      }
    }, 100);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      const newFiles = files.map(file => ({
        id: Date.now() + Math.random(),
        file: file,
        name: file.name,
        size: file.size,
        type: file.type
      }));
      setAttachedFiles(prev => [...prev, ...newFiles]);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (fileId) => {
    setAttachedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      const newFiles = files.map(file => ({
        id: Date.now() + Math.random(),
        file: file,
        name: file.name,
        size: file.size,
        type: file.type
      }));
      setAttachedFiles(prev => [...prev, ...newFiles]);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleCopyMessage = async (messageText, messageId) => {
    try {
      await navigator.clipboard.writeText(messageText);
      setCopiedMessageId(messageId);
      setTimeout(() => {
        setCopiedMessageId(null);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <>
      <style>{`
        @keyframes typingDot {
          0%, 60%, 100% {
            transform: translateY(0);
            opacity: 0.4;
          }
          30% {
            transform: translateY(-4px);
            opacity: 0.8;
          }
        }
        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 0.7;
          }
          50% {
            transform: scale(1.2);
            opacity: 1;
          }
        }
        @keyframes fadeInOut {
          0%, 100% {
            opacity: 0.6;
          }
          50% {
            opacity: 1;
          }
        }
      `}</style>
      <div 
        className="write-page-container"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
      {/* Header - Always visible, styled like Ask page */}
      <div className="chat-header">
        <div>
          <h1 className="chat-title">Write</h1>
          <p className="chat-subtitle">AI-powered writing assistant for emails, documents, and professional communication</p>
        </div>
        {messages.length > 0 && (
          <button 
            className="reset-btn"
            onClick={handleReset}
            type="button"
          >
            <RotateCcw style={{ width: '14px', height: '14px' }} />
            <span>New conversation</span>
          </button>
        )}
      </div>

      {/* Messages Container - Scrollable */}
      <div 
        ref={chatContainerRef}
        className="write-messages-container"
        style={{
          padding: messages.length === 0 ? '0' : '40px 24px',
          paddingBottom: messages.length === 0 ? '0' : '100px'
        }}
      >
        {/* Content wrapper with max-width for readability */}
        <div className="write-content-wrapper">
        {messages.length === 0 && !loading && (
          <>
            <div className="write-empty-state">
              <div className="write-empty-icon-wrapper">
                <PenTool style={{ width: '48px', height: '48px', color: 'var(--primary)' }} />
              </div>
              <h2 className="write-empty-heading">What can I help with today?</h2>
              <p className="write-empty-description">
                I can help you draft emails, responses, documents and other professional writing. 
                Upload a file to chat about it, or just describe what you need!
              </p>
              {examplePrompts.length > 0 && (
                <div className="write-example-prompts">
                  {examplePrompts.map((prompt, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => handleExampleClick(prompt)}
                      className="write-example-prompt-btn"
                    >
                      {prompt}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Input Box - Fixed at bottom when no messages */}
            <div className="write-input-empty-wrapper">
              {/* Attached Files Display */}
              {attachedFiles.length > 0 && (
                <div className="write-attached-files-container">
                  {attachedFiles.map((file) => (
                    <div
                      key={file.id}
                      className="write-file-chip"
                    >
                      <File style={{ width: '14px', height: '14px', color: 'var(--primary)' }} />
                      <span className="write-file-chip-name">
                        {file.name}
                      </span>
                      <span className="write-file-chip-size">
                        ({formatFileSize(file.size)})
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveFile(file.id)}
                        className="write-file-chip-remove"
                      >
                        <X style={{ width: '14px', height: '14px' }} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Input Container */}
              <div className="write-input-container">
                <form className="write-input-form" onSubmit={handleSubmit}>
                  <div className={`write-input-box ${isDragging ? 'dragging' : ''}`}>
                    {/* File Upload Button */}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="write-input-button"
                    >
                      <Paperclip style={{ width: '16px', height: '16px' }} />
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={handleFileSelect}
                      style={{ display: 'none' }}
                    />

                    {/* Textarea */}
                    <textarea
                      ref={textareaRef}
                      value={draft}
                      onChange={(e) => setDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSubmit(e);
                        }
                      }}
                      placeholder="Ask me anything or upload a file..."
                      disabled={loading}
                      rows={1}
                      className="write-input-textarea"
                    />

                    {/* Send Button */}
                    <button
                      type="submit"
                      disabled={(!draft.trim() && attachedFiles.length === 0) || loading}
                      className="write-input-send-button"
                    >
                      {loading ? (
                        <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 0.8s linear infinite' }} />
                      ) : (
                        <Send style={{ width: '16px', height: '16px' }} />
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </>
        )}

        {messages.map((msg, idx) => (
          <div 
            key={msg.id}
            className={`write-message-wrapper ${msg.role}`}
          >
            <div className={`write-message-bubble ${msg.role}`}>
              {/* Show attached files for user messages */}
              {msg.role === 'user' && msg.attachedFiles && msg.attachedFiles.length > 0 && (
                <div className={`write-message-attached-files ${msg.role}`}>
                  {msg.attachedFiles.map((file, fileIdx) => (
                    <span
                      key={fileIdx}
                      className={`write-message-file-tag ${msg.role}`}
                    >
                      <FileText style={{ width: '12px', height: '12px' }} />
                      {file.name}
                    </span>
                  ))}
                </div>
              )}
              
              <div className="write-message-text">
                {msg.text.split('\n').map((line, lineIdx) => (
                  <React.Fragment key={lineIdx}>
                    {line}
                    {lineIdx < msg.text.split('\n').length - 1 && <br />}
                  </React.Fragment>
                ))}
              </div>
              
              {/* Show RAG documents if available and relevant */}
              {msg.role === 'assistant' && msg.ragDocuments && Array.isArray(msg.ragDocuments) && msg.ragDocuments.length > 0 && (
                <div className="write-rag-documents">
                  <div className="write-rag-documents-header">
                    <File style={{ width: '12px', height: '12px' }} />
                    <span>Referenced from knowledge base:</span>
                  </div>
                  <div className="write-rag-documents-list">
                    {msg.ragDocuments.map((docName, idx) => (
                      <span
                        key={idx}
                        className="write-rag-document-tag"
                      >
                        {docName}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="write-message-footer">
                <div className={`write-message-timestamp ${msg.role}`}>
                  {formatTime(msg.timestamp)}
                </div>
                {msg.role === 'assistant' && (
                  <button
                    onClick={() => handleCopyMessage(msg.text, msg.id)}
                    className={`write-copy-button ${copiedMessageId === msg.id ? 'copied' : ''}`}
                    title={copiedMessageId === msg.id ? 'Copied!' : 'Copy message'}
                  >
                    {copiedMessageId === msg.id ? (
                      <Check style={{ width: '14px', height: '14px' }} />
                    ) : (
                      <Copy style={{ width: '14px', height: '14px' }} />
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}

        {loading && (
          <div className="write-loading-message">
            <div className="write-loading-bubble">
              <div className="write-loading-dots">
                <div className="write-loading-dot" />
                <div className="write-loading-dot" />
                <div className="write-loading-dot" />
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="write-error-message">
            <div className="write-error-bubble">
              <AlertCircle style={{ width: '14px', height: '14px', flexShrink: 0 }} />
              <span>{error}</span>
            </div>
          </div>
        )}

        <div ref={chatEndRef} />
        </div>
      </div>

      {/* Floating Input Box - Only show when messages exist */}
      {messages.length > 0 && (
        <div className="write-input-fixed-wrapper">
          <div className="write-input-fixed-inner">
            {/* Attached Files Display */}
            {attachedFiles.length > 0 && (
              <div className="write-attached-files-container fixed">
                {attachedFiles.map((file) => (
                  <div
                    key={file.id}
                    className="write-file-chip"
                  >
                    <File style={{ width: '14px', height: '14px', color: 'var(--primary)' }} />
                    <span className="write-file-chip-name">
                      {file.name}
                    </span>
                    <span className="write-file-chip-size">
                      ({formatFileSize(file.size)})
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveFile(file.id)}
                      className="write-file-chip-remove"
                    >
                      <X style={{ width: '14px', height: '14px' }} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Floating Input Container */}
            <div className="write-input-container">
              <form className="write-input-form" onSubmit={handleSubmit}>
                <div className={`write-input-box ${isDragging ? 'dragging' : ''}`}>
                  {/* File Upload Button */}
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="write-input-button"
                  >
                    <Paperclip style={{ width: '16px', height: '16px' }} />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />

                  {/* Textarea */}
                  <textarea
                    ref={textareaRef}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSubmit(e);
                      }
                    }}
                    placeholder="Type your message..."
                    disabled={loading}
                    rows={1}
                    className="write-input-textarea"
                  />

                  {/* Send Button */}
                  <button
                    type="submit"
                    disabled={(!draft.trim() && attachedFiles.length === 0) || loading}
                    className="write-input-send-button"
                  >
                    {loading ? (
                      <Loader2 style={{ width: '16px', height: '16px', animation: 'spin 0.8s linear infinite' }} />
                    ) : (
                      <Send style={{ width: '16px', height: '16px' }} />
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
}

export default Write;
